import React, { useEffect, useState } from 'react';
import SelfApprovedChip from '../common/SelfApprovedChip';
import FlowStrip from '../../help/FlowStrip';
import StatusStagesChip from '../../help/StatusStagesChip';
import { purchaseFlow } from '../../help/flows';
import { REQUISITION_STAGES } from '../../help/statusStages';
import { format, parseISO } from 'date-fns';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, Button, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { RequisitionService } from '../../services/requisition.service';
import { Requisition, REQUISITION_STATUS_LABELS } from '../../interfaces/requisition.interface';

interface ApprovalQuantities {
    [key: string]: number;
}

const RequisitionDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [requisition, setRequisition] = useState<Requisition | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>('');
    const [approvalQuantities, setApprovalQuantities] = useState<ApprovalQuantities>({});
    const isPending = location.pathname.includes('pending-requisitions') ||
                     (location.state as { from?: string })?.from === '/pending-requisitions';

    useEffect(() => {
        if (id) {
            fetchRequisitionDetails(id);
        }
    }, [id]);

    useEffect(() => {
        if (requisition?.details) {
            const initialQuantities = requisition.details.reduce((acc, detail) => {
                acc[detail.id!] = detail.requiredQty;
                return acc;
            }, {} as ApprovalQuantities);
            setApprovalQuantities(initialQuantities);
        }
    }, [requisition]);

    const fetchRequisitionDetails = async (requisitionId: string) => {
        try {
            setLoading(true);
            const data = await RequisitionService.getRequisitionById(requisitionId);
            setRequisition(data);
            setError('');
        } catch (err) {
            setError('Failed to load requisition details');
            console.error('Error fetching requisition details:', err);
        } finally {
            setLoading(false);
        }
    };

    // const handleApprove = async () => {
    //     if (!requisition?.id || !requisition.details) return;
    //
    //     try {
    //         const approvalItems = requisition.details.map(detail => ({
    //             id: detail.id!,
    //             itemId: detail.itemId,
    //             approvedQty: approvalQuantities[detail.id!] || detail.requiredQty
    //         }));
    //
    //         await RequisitionService.approveRequisition(requisition.id, approvalItems);
    //         navigate('/pending-requisitions');
    //     } catch (error) {
    //         console.error('Error approving requisition:', error);
    //         setError('Failed to approve requisition');
    //     }
    // };
    //
    // const handleReject = async () => {
    //     if (!requisition?.id) return;
    //
    //     try {
    //         await RequisitionService.rejectRequisition(requisition.id);
    //         navigate('/pending-requisitions');
    //     } catch (error) {
    //         console.error('Error rejecting requisition:', error);
    //         setError('Failed to reject requisition');
    //     }
    // };

    const handleQuantityChange = (detailId: string, value: string) => {
        const quantity = parseInt(value);
        if (!isNaN(quantity) && quantity >= 0) {
            setApprovalQuantities(prev => ({
                ...prev,
                [detailId]: quantity
            }));
        }
    };

    const formatDate = (dateString: string) => {
        return format(parseISO(dateString), 'dd-MM-yyyy');
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    if (error) {
        return (
            <Box sx={{ p: 3, color: 'error.main' }}>
                {error}
            </Box>
        );
    }

    if (!requisition) {
        return (
            <Box sx={{ p: 3 }}>
                Requisition not found
            </Box>
        );
    }

    return (
        <div style={{ padding: '20px' }} data-help-page="store.req.page">
            <FlowStrip data-help="store.req.flow" stages={purchaseFlow({ current: 'requisition', requisition: { no: requisition?.refNo ?? 'Requisition' } })} />
            <Box sx={{ mb: 4 }}>
                <Button
                    data-help="store.req.back" startIcon={<ArrowBack />}
                    onClick={() => navigate('/my-requisitions')}
                    sx={{ mb: 2 }}
                >
                    Back to Requisitions
                </Button>

                <Typography variant="h4" gutterBottom>
                    Requisition Details
                </Typography>
            </Box>

            <Paper sx={{ p: 3, mb: 3 }} data-help-section="store.req.header">
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Typography>
                        <strong>Reference No:</strong> {requisition?.refNo}
                    </Typography>
                    <Typography component="div">
                        <strong>Status:</strong>{' '}
                        <StatusStagesChip
                            data-help="store.req.statusStages"
                            label={requisition?.status ? REQUISITION_STATUS_LABELS[requisition.status] : '-'}
                            status={requisition?.status ?? ''}
                            stages={REQUISITION_STAGES}
                            color={requisition?.status === 'ISSUED' ? 'success' : 'info'}
                        />
                        <SelfApprovedChip selfApproved={requisition?.selfApproved} rule={requisition?.selfApprovalRule} />
                    </Typography>
                    <Typography>
                        <strong>Date:</strong> {requisition?.date ? formatDate(requisition.date) : '-'}
                    </Typography>
                    <Typography>
                        <strong>Requisition By:</strong> {requisition?.requisitionByName}
                    </Typography>
                    {requisition?.remarks && (
                        <Typography sx={{ gridColumn: '1 / -1', whiteSpace: 'pre-wrap' }}>
                            <strong>Purpose / remarks:</strong> {requisition.remarks}
                        </Typography>
                    )}
                    {requisition?.status === 'REJECTED' && requisition.rejectionReason && (
                        <Typography color="error" sx={{ gridColumn: '1 / -1', whiteSpace: 'pre-wrap' }}>
                            Rejected: {requisition.rejectionReason}
                        </Typography>
                    )}
                </Box>
            </Paper>

            {/*<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>*/}
            {/*    <Typography variant="h6">Items</Typography>*/}
            {/*    {isPending && (*/}
            {/*        <Box sx={{ display: 'flex', gap: 2 }}>*/}
            {/*            <Button*/}
            {/*                variant="contained"*/}
            {/*                color="error"*/}
            {/*                startIcon={<Close />}*/}
            {/*                onClick={handleReject}*/}
            {/*            >*/}
            {/*                Reject*/}
            {/*            </Button>*/}
            {/*            <Button*/}
            {/*                variant="contained"*/}
            {/*                color="success"*/}
            {/*                startIcon={<Check />}*/}
            {/*                onClick={handleApprove}*/}
            {/*            >*/}
            {/*                Approve*/}
            {/*            </Button>*/}
            {/*        </Box>*/}
            {/*    )}*/}
            {/*</Box>*/}

            <TableContainer component={Paper} data-help-section="store.req.lines">
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="store.reqLine.col.item">Item Name</TableCell>
                            <TableCell data-help="store.reqLine.col.required" align="right">Required Qty</TableCell>
                            {isPending ? (
                                <TableCell data-help="store.reqLine.col.approveInput" align="right">Approve Qty</TableCell>
                            ) : (
                                <>
                                    <TableCell data-help="store.reqLine.col.approved" align="right">Approved Qty</TableCell>
                                    <TableCell data-help="store.reqLine.col.approvedDate">Approved Date</TableCell>
                                    <TableCell data-help="store.reqLine.col.issuedDate">Issued Date</TableCell>
                                </>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {requisition?.details?.map((detail) => (
                            <TableRow key={detail.id}>
                                <TableCell>{detail.itemName}</TableCell>
                                <TableCell align="right">{detail.requiredQty}</TableCell>
                                {isPending ? (
                                    <TableCell align="right">
                                        <TextField
                                            type="number"
                                            data-help="store.reqLine.field.approveQty" value={approvalQuantities[detail.id!] || ''}
                                            onChange={(e) => handleQuantityChange(detail.id!, e.target.value)}
                                            InputProps={{
                                                inputProps: { min: 0, max: detail.requiredQty }
                                            }}
                                            size="small"
                                        />
                                    </TableCell>
                                ) : (
                                    <>
                                        <TableCell align="right">{detail.approvedQty || '-'}</TableCell>
                                        <TableCell>
                                            {detail.approvedDate ? formatDate(detail.approvedDate) : '-'}
                                        </TableCell>
                                        <TableCell>
                                            {detail.issuedDate ? formatDate(detail.issuedDate) : '-'}
                                        </TableCell>
                                    </>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {error && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#ffebee', color: '#c62828', borderRadius: 1 }}>
                    {error}
                </Box>
            )}
        </div>
    );
};

export default RequisitionDetails;

